<!-- RESULTS -->
<app-content
    *appHasPermissionId="permissionList.kpiResultRead"
    contentTitle="{{ 'translate_results' | translate }}"
    class="mb-5"
>
    <!-- Tools -->
    <ng-container tools *appHasPermissionId="permissionList.kpiResultWrite">
        @if (kpi) {
            <a
                (click)="beforeNavigate.emit()"
                class="btn btn-sm btn-outline-white"
                [routerLink]="[
                    '',
                    'kpi',
                    'update-result',
                    $safeNavigationMigration($safeNavigationMigration(kpi?.id)),
                ]"
                [queryParams]="{
                    year: currentYear,
                    departmentId: kpi.owningDepartment.id,
                }"
            >
                <i class="fa-light fa-plus"></i>
                <span> {{ 'translate_add_new_year' | translate }}</span>
            </a>
        }
    </ng-container>

    <!-- Content -->
    <app-list-loading content [items]="$safeNavigationMigration(kpi?.results)">
        <!-- View Mode Toggle Group -->
        <div class="mb-6 flex items-center justify-center">
            <div
                class="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1"
            >
                @for (item of resultViewModeList; track item) {
                    <button
                        type="button"
                        (click)="resultViewMode = item"
                        class="rounded-md px-4 py-2 text-sm font-medium transition-all"
                        [ngClass]="{
                            'bg-white text-primary-700 shadow-sm':
                                resultViewMode === item,
                            'text-slate-600 hover:text-slate-800':
                                resultViewMode !== item,
                        }"
                    >
                        {{ 'translate_result_view_mode_' + item | translate }}
                    </button>
                }
            </div>
        </div>

        @switch (resultViewMode) {
            <!-- All results -->
            @case ('all') {
                <ng-container
                    [ngTemplateOutlet]="allResultsTemplate"
                ></ng-container>
            }
            <!-- Grouped by departments -->
            @case ('by_department') {
                <ng-container
                    [ngTemplateOutlet]="resultsGroupedByDepartmentsTemplate"
                ></ng-container>
            }
            <!-- Grouped by year -->
            @case ('by_year') {
                <ng-container
                    [ngTemplateOutlet]="resultsGroupedByYearTemplate"
                ></ng-container>
            }
        }
    </app-list-loading>
</app-content>

<!-- A template for listing all results -->
<ng-template #allResultsTemplate>
    @if (kpi) {
        <app-kpi-results-table
            [rows]="allResultRows"
            [shownResultIds]="shownResultIds"
            (shownResultIdsChange)="shownResultIds = $event"
            [showYear]="true"
            [showDepartment]="true"
            [isTrend]="kpi.isTrend"
        >
            <ng-template #details let-row="row">
                <ng-container
                    [ngTemplateOutlet]="richDetailsTemplate"
                    [ngTemplateOutletContext]="{ row: row }"
                ></ng-container>
            </ng-template>
            <ng-template #actions let-row="row">
                <ng-container
                    [ngTemplateOutlet]="richActionsTemplate"
                    [ngTemplateOutletContext]="{ row: row }"
                ></ng-container>
            </ng-template>
            <ng-template #rowActions let-row="row">
                <ng-container
                    [ngTemplateOutlet]="richRowActionsTemplate"
                    [ngTemplateOutletContext]="{ row: row }"
                ></ng-container>
            </ng-template>
        </app-kpi-results-table>
    }
</ng-template>

<!-- A template for listing all results grouped by department -->
<ng-template #resultsGroupedByDepartmentsTemplate>
    <div class="flex flex-col gap-4">
        @for (item of resultsGroupedByDepartments; track item) {
            <div>
                <!-- Department name header -->
                <div
                    class="flex items-center gap-2 px-5 py-3 font-semibold text-slate-800"
                >
                    <i class="fa-light fa-building text-slate-400"></i>
                    {{ item.department.name }}
                </div>
                @if (kpi) {
                    <app-kpi-results-table
                        [rows]="item.rows"
                        [shownResultIds]="shownResultIds"
                        (shownResultIdsChange)="shownResultIds = $event"
                        [showYear]="true"
                        [showDepartment]="false"
                        [isTrend]="kpi.isTrend"
                    >
                        <ng-template #details let-row="row">
                            <ng-container
                                [ngTemplateOutlet]="richDetailsTemplate"
                                [ngTemplateOutletContext]="{ row: row }"
                            ></ng-container>
                        </ng-template>
                        <ng-template #actions let-row="row">
                            <ng-container
                                [ngTemplateOutlet]="richActionsTemplate"
                                [ngTemplateOutletContext]="{ row: row }"
                            ></ng-container>
                        </ng-template>
                        <ng-template #rowActions let-row="row">
                            <ng-container
                                [ngTemplateOutlet]="richRowActionsTemplate"
                                [ngTemplateOutletContext]="{ row: row }"
                            ></ng-container>
                        </ng-template>
                    </app-kpi-results-table>
                }
            </div>
        }
    </div>
</ng-template>

<!-- A template for listing all results grouped by year -->
<ng-template #resultsGroupedByYearTemplate>
    <div class="flex flex-col gap-4">
        @for (item of resultsGroupedByYear; track item) {
            <div>
                <!-- Year header -->
                <div
                    class="flex items-center gap-2 px-5 py-3 font-semibold text-slate-800"
                >
                    <i class="fa-light fa-calendar text-slate-400"></i>
                    {{ item.year }}
                </div>
                @if (kpi) {
                    <app-kpi-results-table
                        [rows]="item.rows"
                        [shownResultIds]="shownResultIds"
                        (shownResultIdsChange)="shownResultIds = $event"
                        [showYear]="false"
                        [showDepartment]="true"
                        [isTrend]="kpi.isTrend"
                    >
                        <ng-template #details let-row="row">
                            <ng-container
                                [ngTemplateOutlet]="richDetailsTemplate"
                                [ngTemplateOutletContext]="{ row: row }"
                            ></ng-container>
                        </ng-template>
                        <ng-template #actions let-row="row">
                            <ng-container
                                [ngTemplateOutlet]="richActionsTemplate"
                                [ngTemplateOutletContext]="{ row: row }"
                            ></ng-container>
                        </ng-template>
                        <ng-template #rowActions let-row="row">
                            <ng-container
                                [ngTemplateOutlet]="richRowActionsTemplate"
                                [ngTemplateOutletContext]="{ row: row }"
                            ></ng-container>
                        </ng-template>
                    </app-kpi-results-table>
                }
            </div>
        }
    </div>
</ng-template>

<!-- A template that shows a single result's count badges (التفاصيل column) -->
<ng-template #richActionsTemplate let-row="row">
    <div class="flex flex-col items-center gap-1.5 py-1.5">
        <button
            type="button"
            (click)="showResultCapabilities(row.original)"
            class="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-200"
        >
            <i class="fa-light fa-layer-group text-slate-400"></i>
            {{ row.original.capabilityCount }}
        </button>
        <button
            type="button"
            (click)="showResultAttachments(row.original)"
            class="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-200"
        >
            <i class="fa-light fa-paperclip text-slate-400"></i>
            {{ row.original.libraryFileCount }}
        </button>
    </div>
</ng-template>

<!-- A template that shows a single result's action buttons (separate column) -->
<ng-template #richRowActionsTemplate let-row="row">
    <div class="flex items-center justify-center gap-1">
        <!-- Edit button -->
        <a
            *appHasPermissionId="permissionList.kpiResultEntry"
            (click)="beforeNavigate.emit()"
            class="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-primary-600"
            [routerLink]="[
                '',
                'kpi',
                'update-result',
                $safeNavigationMigration($safeNavigationMigration(kpi?.id)),
            ]"
            [queryParams]="{
                year: row.year,
                departmentId: row.original.department.id,
            }"
            [appTooltip]="'translate_edit' | translate"
        >
            <i class="fa-light fa-edit"></i>
        </a>

        <!-- Period breakdown button -->
        <button
            type="button"
            [appTooltip]="'translate_period_breakdown' | translate"
            (click)="showPeriodsBreakdownDialog(row.original)"
            class="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-primary-600"
        >
            <i class="fa-light fa-table-cells"></i>
        </button>
    </div>
</ng-template>

<!-- A template that shows a single result's rich details -->
<ng-template #richDetailsTemplate let-row="row">
    <div class="border-t border-slate-100 p-5">
        <div class="flex flex-col gap-5">
            <!-- Alert -->
            <div
                class="flex items-start gap-4 rounded-xl border border-amber-200 bg-amber-50 p-4"
            >
                <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100"
                >
                    <i class="fa-light fa-lightbulb text-amber-600"></i>
                </div>
                <div class="flex-1">
                    <p class="font-medium text-amber-800">
                        {{ 'translate_note' | translate }}
                    </p>
                    <p class="mt-0.5 text-sm text-amber-700">
                        {{
                            'translate_kpi_result_calculation_note' | translate
                        }}
                    </p>
                </div>
            </div>

            <!-- Results periods -->
            <div
                class="overflow-hidden rounded-xl border border-slate-200 bg-white"
            >
                <div class="overflow-x-auto">
                    <table class="w-full table-fixed">
                        <!-- Header -->
                        <thead>
                            <tr class="border-b border-slate-100 bg-slate-50">
                                <th
                                    class="w-48 px-4 py-3 text-start text-xs font-semibold uppercase tracking-wider text-slate-500"
                                >
                                    {{
                                        'translate_target_description'
                                            | translate
                                    }}
                                </th>
                                @for (
                                    period of row.original.periods;
                                    track period;
                                    let idx = $index
                                ) {
                                    <th
                                        class="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider"
                                        [ngClass]="{
                                            'bg-primary-50 text-primary-700':
                                                row.original.currentPeriod >
                                                idx,
                                            'text-slate-500':
                                                row.original.currentPeriod <=
                                                idx,
                                        }"
                                    >
                                        {{
                                            (row.original.measurementCycle ===
                                            'annual'
                                                ? 'translate_values'
                                                : row.original
                                                        .measurementCycle ===
                                                    'semi_annual'
                                                  ? 'translate_half_year'
                                                  : row.original
                                                          .measurementCycle ===
                                                      'quarter'
                                                    ? 'translate_quarter'
                                                    : 'translate_month'
                                            ) | translate
                                        }}
                                        @if (
                                            row.original.measurementCycle !==
                                            'annual'
                                        ) {
                                            {{ period?.period + 1 }}
                                        }
                                    </th>
                                }
                                <th
                                    class="bg-slate-100 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-700"
                                >
                                    {{ 'translate_total' | translate }}
                                </th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-slate-100">
                            <!-- Target -->
                            @if (!kpi.isTrend) {
                                <tr class="hover:bg-slate-50/50">
                                    <td class="px-4 py-3">
                                        <div class="font-medium text-slate-700">
                                            {{ 'translate_target' | translate }}
                                        </div>
                                        <div
                                            class="mt-0.5 text-xs text-slate-500"
                                        >
                                            {{
                                                row.original.targetSettingMethod
                                                    .name
                                            }}
                                        </div>
                                    </td>
                                    @for (
                                        period of row.original.periods;
                                        track period;
                                        let idx = $index
                                    ) {
                                        <td
                                            class="px-3 py-3 text-center text-sm"
                                            [ngClass]="{
                                                'bg-primary-50/50':
                                                    row.original.currentPeriod >
                                                    idx,
                                            }"
                                        >
                                            {{
                                                period.target
                                                    | formatKpiResult
                                                        : row.original.units
                                                        : row.original
                                                              .unitsDescription
                                                        : true
                                                        : row.original
                                                              .decimalPlaces
                                            }}
                                        </td>
                                    }
                                    <td
                                        class="bg-slate-50 px-3 py-3 text-center text-sm font-semibold text-slate-800"
                                    >
                                        {{
                                            row.original.target
                                                | formatKpiResult
                                                    : row.original.units
                                                    : row.original
                                                          .unitsDescription
                                                    : true
                                                    : row.original.decimalPlaces
                                        }}
                                    </td>
                                </tr>
                            }

                            @if (
                                row.original.units !== 'number' &&
                                row.original.formula !== 'A'
                            ) {
                                <!-- A -->
                                <tr class="hover:bg-slate-50/50">
                                    <td
                                        class="px-4 py-3 text-sm text-slate-600"
                                    >
                                        {{
                                            row.original.formulaDescriptionA +
                                                ' (A)'
                                        }}
                                    </td>
                                    @for (
                                        period of row.original.periods;
                                        track period;
                                        let idx = $index
                                    ) {
                                        <td
                                            class="px-3 py-3 text-center text-sm text-slate-600"
                                            [ngClass]="{
                                                'bg-primary-50/50':
                                                    row.original.currentPeriod >
                                                    idx,
                                            }"
                                        >
                                            {{
                                                period.a
                                                    | formatKpiResult
                                                        : 'number'
                                                        : ''
                                                        : true
                                                        : row.original
                                                              .decimalPlaces
                                            }}
                                        </td>
                                    }
                                    <td
                                        class="bg-slate-50 px-3 py-3 text-center text-sm text-slate-700"
                                    >
                                        {{
                                            row.original.aggregateA
                                                | formatKpiResult
                                                    : 'number'
                                                    : ''
                                                    : true
                                                    : row.original.decimalPlaces
                                        }}
                                    </td>
                                </tr>
                                <!-- B -->
                                <tr class="hover:bg-slate-50/50">
                                    <td
                                        class="px-4 py-3 text-sm text-slate-600"
                                    >
                                        {{
                                            row.original.formulaDescriptionB +
                                                ' (B)'
                                        }}
                                    </td>
                                    @for (
                                        period of row.original.periods;
                                        track period;
                                        let idx = $index
                                    ) {
                                        <td
                                            class="px-3 py-3 text-center text-sm text-slate-600"
                                            [ngClass]="{
                                                'bg-primary-50/50':
                                                    row.original.currentPeriod >
                                                    idx,
                                            }"
                                        >
                                            {{
                                                period.b
                                                    | formatKpiResult
                                                        : 'number'
                                                        : ''
                                                        : true
                                                        : row.original
                                                              .decimalPlaces
                                            }}
                                        </td>
                                    }
                                    <td
                                        class="bg-slate-50 px-3 py-3 text-center text-sm text-slate-700"
                                    >
                                        {{
                                            row.original.aggregateB
                                                | formatKpiResult
                                                    : 'number'
                                                    : ''
                                                    : true
                                                    : row.original.decimalPlaces
                                        }}
                                    </td>
                                </tr>
                            }

                            <!-- Result -->
                            <tr class="hover:bg-slate-50/50">
                                <td
                                    class="px-4 py-3 font-medium text-slate-700"
                                >
                                    {{ 'translate_result' | translate }}
                                </td>
                                @for (
                                    period of row.original.periods;
                                    track period;
                                    let idx = $index
                                ) {
                                    <td
                                        class="px-3 py-3 text-center text-sm font-medium text-slate-700"
                                        [ngClass]="{
                                            'bg-primary-50/50':
                                                row.original.currentPeriod >
                                                idx,
                                        }"
                                    >
                                        {{
                                            period.result
                                                | formatKpiResult
                                                    : row.original.units
                                                    : row.original
                                                          .unitsDescription
                                                    : true
                                                    : row.original.decimalPlaces
                                        }}
                                    </td>
                                }
                                <td
                                    class="bg-slate-50 px-3 py-3 text-center text-sm font-bold text-slate-800"
                                >
                                    {{
                                        row.original.result
                                            | formatKpiResult
                                                : row.original.units
                                                : row.original.unitsDescription
                                                : true
                                                : row.original.decimalPlaces
                                    }}
                                </td>
                            </tr>

                            <!-- Achieved -->
                            @if (!kpi.isTrend) {
                                <tr class="full-table-row bg-slate-50/30">
                                    <td
                                        class="px-4 py-3 font-medium text-slate-700"
                                    >
                                        {{ 'translate_achieved' | translate }}
                                    </td>
                                    @for (
                                        period of row.original.periods;
                                        track period;
                                        let idx = $index
                                    ) {
                                        <td class="full-table-cell text-center">
                                            <app-achieved-container
                                                [achieved]="period.achieved"
                                                [decimalPlaces]="
                                                    row.original.decimalPlaces
                                                "
                                            ></app-achieved-container>
                                        </td>
                                    }
                                    <td class="full-table-cell text-center">
                                        <app-achieved-container
                                            [achieved]="row.original.achieved"
                                            [decimalPlaces]="
                                                row.original.decimalPlaces
                                            "
                                        ></app-achieved-container>
                                    </td>
                                </tr>
                            }

                            <!-- Evaluate -->
                            <ng-container>
                                @if (
                                    row.original.periods[0]
                                        .evaluateActionAbility.canRead
                                ) {
                                    <tr
                                        class="full-table-row hover:bg-slate-50/50"
                                    >
                                        <td
                                            class="px-4 py-3 font-medium text-slate-700"
                                        >
                                            {{
                                                'translate_evaluation_result'
                                                    | translate
                                            }}
                                        </td>
                                        @if (
                                            kpi?.isExemptFromEvaluation ===
                                                false &&
                                            row.original
                                                ?.isExemptFromEvaluation ===
                                                false
                                        ) {
                                            @for (
                                                period of row.original.periods;
                                                track period;
                                                let idx = $index
                                            ) {
                                                <td
                                                    class="full-table-cell text-center"
                                                >
                                                    <div
                                                        class="flex h-full items-center justify-center gap-2 px-3 py-3"
                                                        [style.background-color]="
                                                            period.evaluationScoreDetail
                                                                ? getLightBgColor(
                                                                      $safeNavigationMigration(
                                                                          period
                                                                              .evaluationScoreDetail
                                                                              ?.color
                                                                      )
                                                                  )
                                                                : null
                                                        "
                                                    >
                                                        <button
                                                            type="button"
                                                            (click)="
                                                                showEvaluationPopup(
                                                                    period,
                                                                    row.original
                                                                        .year
                                                                )
                                                            "
                                                            class="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-primary-600"
                                                        >
                                                            <i
                                                                class="fa-light fa-list-check text-sm"
                                                            ></i>
                                                        </button>
                                                        @if (
                                                            period.evaluationScoreDetail;
                                                            as evaluationScoreDetail
                                                        ) {
                                                            <span
                                                                class="text-xs font-semibold"
                                                                [style.color]="
                                                                    evaluationScoreDetail?.color
                                                                "
                                                            >
                                                                {{
                                                                    evaluationScoreDetail.score *
                                                                        100
                                                                        | round
                                                                            : 2
                                                                }}%
                                                            </span>
                                                        }
                                                    </div>
                                                </td>
                                            }
                                            <!-- Total Periods Evaluations -->
                                            <td
                                                class="full-table-cell text-center"
                                            >
                                                <div
                                                    class="flex h-full items-center justify-center px-3 py-3"
                                                    [style.background-color]="
                                                        row.original
                                                            .totalPeriodsEvaluationScore
                                                            ? getLightBgColor(
                                                                  $safeNavigationMigration(
                                                                      row
                                                                          .original
                                                                          .totalPeriodsEvaluationScore
                                                                          ?.color
                                                                  )
                                                              )
                                                            : null
                                                    "
                                                >
                                                    @if (
                                                        row.original
                                                            .totalPeriodsEvaluationScore
                                                    ) {
                                                        <span
                                                            class="text-xs font-semibold"
                                                            [style.color]="
                                                                row.original
                                                                    .totalPeriodsEvaluationScore
                                                                    ?.color
                                                            "
                                                        >
                                                            {{
                                                                row.original
                                                                    .totalPeriodsEvaluationScore
                                                                    .score * 100
                                                                    | round
                                                                        : row
                                                                              .original
                                                                              .decimalPlaces
                                                            }}%
                                                        </span>
                                                    }
                                                </div>
                                            </td>
                                        }
                                        @if (
                                            kpi?.isExemptFromEvaluation ===
                                                true ||
                                            row.original
                                                ?.isExemptFromEvaluation ===
                                                true
                                        ) {
                                            <td
                                                [attr.colspan]="
                                                    row.original.periods
                                                        .length + 1
                                                "
                                                class="px-3 py-3 text-center text-sm italic text-slate-400"
                                            >
                                                {{
                                                    'translate_is_exempt_from_evaluation'
                                                        | translate
                                                }}
                                            </td>
                                        }
                                    </tr>
                                }
                            </ng-container>

                            <!-- Result analysis -->
                            <tr class="hover:bg-slate-50/50">
                                <td class="px-4 py-3 text-sm text-slate-600">
                                    {{
                                        'translate_result_analysis' | translate
                                    }}
                                </td>
                                @for (
                                    period of row.original.periods;
                                    track period;
                                    let idx = $index
                                ) {
                                    <td
                                        class="px-3 py-3 text-center text-sm text-slate-600"
                                        [ngClass]="{
                                            'bg-primary-50/50':
                                                row.original.currentPeriod >
                                                idx,
                                        }"
                                    >
                                        {{
                                            getResultAnalysisForCurrentLanguage(
                                                period
                                            )
                                        }}
                                    </td>
                                }
                                <td class="bg-slate-50 px-3 py-3"></td>
                            </tr>

                            <!-- Improvement procedures -->
                            <tr class="hover:bg-slate-50/50">
                                <td class="px-4 py-3 text-sm text-slate-600">
                                    {{
                                        'translate_improvement_procedures'
                                            | translate
                                    }}
                                </td>
                                @for (
                                    period of row.original.periods;
                                    track period;
                                    let idx = $index
                                ) {
                                    <td
                                        class="px-3 py-3 text-center text-sm text-slate-600"
                                        [ngClass]="{
                                            'bg-primary-50/50':
                                                row.original.currentPeriod >
                                                idx,
                                        }"
                                    >
                                        {{
                                            getImprovementProcedureForCurrentLanguage(
                                                period
                                            )
                                        }}
                                    </td>
                                }
                                <td class="bg-slate-50 px-3 py-3"></td>
                            </tr>

                            <!-- Improvement procedures completion percentage -->
                            <tr class="hover:bg-slate-50/50">
                                <td class="px-4 py-3 text-sm text-slate-600">
                                    {{
                                        'translate_improvement_procedures_completion_percentage'
                                            | translate
                                    }}
                                </td>
                                @for (
                                    period of row.original.periods;
                                    track period;
                                    let idx = $index
                                ) {
                                    <td
                                        class="px-3 py-3 text-center text-sm"
                                        [ngClass]="{
                                            'bg-primary-50/50':
                                                row.original.currentPeriod >
                                                idx,
                                        }"
                                    >
                                        @if (
                                            period.improvementProcedureCompletionPercentage !==
                                            null
                                        ) {
                                            <span
                                                class="font-medium text-slate-700"
                                            >
                                                {{
                                                    period.improvementProcedureCompletionPercentage
                                                }}%
                                            </span>
                                        }
                                    </td>
                                }
                                <td class="bg-slate-50 px-3 py-3"></td>
                            </tr>

                            <!-- Improvement procedures expected completion date -->
                            <tr class="hover:bg-slate-50/50">
                                <td class="px-4 py-3 text-sm text-slate-600">
                                    {{
                                        'translate_improvement_procedures_expected_completion_date'
                                            | translate
                                    }}
                                </td>
                                @for (
                                    period of row.original.periods;
                                    track period;
                                    let idx = $index
                                ) {
                                    <td
                                        class="px-3 py-3 text-center text-sm text-slate-600"
                                        [ngClass]="{
                                            'bg-primary-50/50':
                                                row.original.currentPeriod >
                                                idx,
                                        }"
                                    >
                                        {{
                                            period.improvementProcedureExpectedCompletionDate
                                                | date
                                        }}
                                    </td>
                                }
                                <td class="bg-slate-50 px-3 py-3"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Leader directives and supervisor notes -->
            <app-period-tab-list
                [periods]="row.original.periods"
                [measurementCycle]="row.original.measurementCycle"
                [contentTemplate]="directiveAndNoteTemplate"
            ></app-period-tab-list>
        </div>
    </div>
</ng-template>

<!-- A template of a period's directives and notes -->
<ng-template #directiveAndNoteTemplate let-period="period">
    <div class="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table class="w-full">
            <thead>
                <tr class="border-b border-slate-100 bg-slate-50">
                    <th
                        class="w-1/2 px-5 py-3 text-start text-xs font-semibold uppercase tracking-wider text-slate-500"
                    >
                        {{ 'translate_supervisor_notes' | translate }}
                    </th>
                    <th
                        class="w-1/2 px-5 py-3 text-start text-xs font-semibold uppercase tracking-wider text-slate-500"
                    >
                        {{ 'translate_leadership_directives' | translate }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr class="hover:bg-slate-50">
                    <td class="px-5 py-4 text-sm text-slate-600">
                        @if (period.supervisorNote) {
                            <span>
                                {{ period.supervisorNote }}
                            </span>
                        } @else {
                            <ng-template
                                [ngTemplateOutlet]="noteSupervisorNotAvailable"
                            ></ng-template>
                        }
                        <ng-template #noteSupervisorNotAvailable>
                            <span class="italic text-slate-400">
                                {{ 'translate_not_available' | translate }}
                            </span>
                        </ng-template>
                    </td>
                    <td class="px-5 py-4 text-sm text-slate-600">
                        @if (period.leadershipDirective) {
                            <span>
                                {{ period.leadershipDirective }}
                            </span>
                        } @else {
                            <ng-template
                                [ngTemplateOutlet]="noteDirectiveNotAvailable"
                            ></ng-template>
                        }
                        <ng-template #noteDirectiveNotAvailable>
                            <span class="italic text-slate-400">
                                {{ 'translate_not_available' | translate }}
                            </span>
                        </ng-template>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</ng-template>
